import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAltHotkeys } from '../../../hooks/useAltHotkeys';
import { useModalDrag } from '../../../hooks/useModalDrag';
import ModalHeader from '../../../components/ui/ModalHeader';
import { ChatBubbleLeftIcon } from '@heroicons/react/24/outline';
import quoteService, { type FscTariff } from '../../../services/quote.service';
import { Tooltip } from '../../../components/ui/Tooltip';

interface Props {
  open: boolean;
  /** Bill-to recno whose FSC tariffs to list. Ignored when `generic`. */
  billto: number;
  onClose: () => void;
  /** Picking a tariff returns its number + current rate + ratetype. */
  onPick: (t: FscTariff) => void;
  /** List EVERY selectable tariff rather than one bill-to's — the Rate
   *  Calculator has no bill-to. Generic rows carry no `currentRate` (resolving
   *  ~280 of them would be ~600-800 queries), so this mode drops the Current
   *  Rate column and adds a filter box in its place; the picked tariff's rate is
   *  resolved separately. */
  generic?: boolean;
}

// STANDARD tariffs are a percentage of the dray (e.g. 22.75%); PER MILE is a
// dollar rate applied per mile (e.g. $0.6900); FLAT is a dollar amount per move
// (e.g. $45.00). Format the current rate to match.
const fmtRate = (r: number | null, ratetype: string | null): string => {
  if (r == null) return '—';
  if (ratetype === 'PER MILE') return `$${r.toFixed(4)}`;
  if (ratetype === 'FLAT') return `$${r.toFixed(2)}`;
  return `${r.toFixed(2)}%`;
};

/**
 * Customer FSC tariff picker (V1 fsctarifflookup). Lists the bill-to's
 * selectable tariffs with their current rate; selecting one sets the quote's
 * "Cust Tariff #" + FSC rate. Portaled to <body> (escapes the editor drawer's
 * transform).
 */
const FscTariffLookupModal: React.FC<Props> = ({ open, billto, onClose, onPick, generic }) => {
  const [rows, setRows] = useState<FscTariff[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    const load = generic
      ? quoteService.listFscTariffs(0, { all: true })
      : quoteService.listFscTariffs(billto);
    load
      .then((r) => { if (!cancelled) setRows(r); })
      .catch(() => { if (!cancelled) setError('Could not load tariffs.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, billto, generic]);

  // Reopening starts from an unfiltered list rather than the last search.
  useEffect(() => { if (!open) setFilter(''); }, [open]);

  // Generic mode only — a bill-to's handful of tariffs needs no filtering.
  // Matches across the identifying columns, since a user may know the tariff by
  // number, by name, or only by "the per-mile Bayport one".
  const shown = React.useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((t) => [
      String(t.tariffNumber), t.name, t.tariffType, t.ratetype, t.otrLocal, t.fuelIndex,
    ].some((f) => (f || '').toLowerCase().includes(q)));
  }, [rows, filter]);

  const columns = generic
    ? ['Tariff #', 'Name/Description', 'Type', 'Index', 'Rate Type', 'OTR/Local', 'Effective', 'Comment', '']
    : ['Tariff #', 'Name/Description', 'Type', 'Index', 'Rate Type', 'OTR/Local', 'Current Rate', 'Comment', ''];

  // Lookup picker: Alt+C closes. Picking a tariff is a row click, not a chord.
  // Opened from RateCalculatorModal, which claims the layer first, so this one
  // sits on top of it and wins while it's open.
  useAltHotkeys({ C: onClose }, undefined, { layer: open });

  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(open);

  if (!open) return null;
  return createPortal(
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-900/40 p-6"
      role="dialog"
      aria-label="FSC Tariff Lookup"
      onClick={onClose}
    >
      <div
        ref={panelRef}
        style={panelStyle}
        className="flex max-h-[80vh] w-full max-w-[860px] flex-col rounded-lg border border-slate-200 bg-panel shadow-xl dark:border-gray-700"
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader title="FSC Tariff Lookup" onClose={onClose} dragHandleProps={dragHandleProps} />
        {generic && !loading && !error && (
          <div className="border-b border-slate-200 px-4 py-2 dark:border-gray-700">
            <input
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              aria-label="Filter tariffs"
              placeholder="Filter by tariff #, name, or type…"
              className="w-full rounded border border-slate-300 px-2 py-1 text-[0.75rem] text-slate-900 placeholder:text-slate-400 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100"
            />
            <div className="pt-1 text-[0.6875rem] text-slate-400 dark:text-slate-500">
              {shown.length} of {rows.length} tariffs · pick one to price it
            </div>
          </div>
        )}
        <div className="overflow-y-auto">
          {loading ? (
            <div className="px-4 py-8 text-center text-[0.78125rem] text-slate-400">Loading…</div>
          ) : error ? (
            <div className="px-4 py-8 text-center text-[0.78125rem] text-red-600 dark:text-red-400">{error}</div>
          ) : rows.length === 0 ? (
            <div className="px-4 py-8 text-center text-[0.78125rem] text-slate-400 dark:text-slate-500">
              {generic ? 'No FSC tariffs found.' : 'No FSC tariffs for this bill-to.'}
            </div>
          ) : shown.length === 0 ? (
            <div className="px-4 py-8 text-center text-[0.78125rem] text-slate-400 dark:text-slate-500">
              No tariffs match that filter.
            </div>
          ) : (
            <table className="w-full text-[0.75rem]">
              <thead className="bg-slate-50 dark:bg-slate-700/60 text-[0.65625rem] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                <tr>
                  {columns.map((h) => (
                    <th key={h} className="px-2.5 py-1.5 text-left whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {shown.map((t) => (
                  <tr key={t.tariffNumber} className="border-t border-slate-100 dark:border-slate-700">
                    <td className="px-2.5 py-1.5 font-semibold text-slate-900 dark:text-slate-100">{t.tariffNumber}</td>
                    <td className="px-2.5 py-1.5">{t.name}</td>
                    <td className="px-2.5 py-1.5">{t.tariffType}</td>
                    <td className="px-2.5 py-1.5">{t.fuelIndex}</td>
                    <td className="px-2.5 py-1.5">{t.ratetype}</td>
                    <td className="px-2.5 py-1.5">{t.otrLocal}</td>
                    {/* Generic rows carry no rate — show the effective date there
                        instead, which is what distinguishes near-identical tariffs. */}
                    <td className="px-2.5 py-1.5">
                      {generic ? (t.effectiveDate || '—') : fmtRate(t.currentRate, t.ratetype)}
                    </td>
                    <td className="px-2.5 py-1.5">
                      {t.comments && (
                        <Tooltip content={<span className="block max-w-xs whitespace-pre-wrap text-left">{t.comments}</span>} placement="left">
                          <ChatBubbleLeftIcon
                            aria-label="Tariff comment"
                            className="h-3.5 w-3.5 text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
                          />
                        </Tooltip>
                      )}
                    </td>
                    <td className="px-2.5 py-1.5 text-right">
                      <button
                        type="button"
                        onClick={() => { onPick(t); onClose(); }}
                        className="rounded bg-blue-600 px-2 py-1 text-[0.6875rem] font-semibold text-white hover:bg-blue-700"
                      >
                        Select
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default FscTariffLookupModal;
